import Payload from './util/Payload';
import Recording from './util/Recording';
import Browser from './util/Browser';
import queryString from 'query-string';
import ReactDOM from 'react-dom';
import EngagementApp from './components/EngagementApp.react';
import get from 'lodash/get';
import React from 'react';
import { state } from './app';
import { didFinishHide, didFinishLoad, didFinishShow, didFinishStartHide } from './setup-e2';

/**
 * Provides a window.injectOmSdkVerificationScript function for use when omsdk_verify=1
 * in the first action of the payload (the queue_interstitial).
 *
 * NOTE!  UPDATE workbox-config.js when you change versions here or omsdk file structure!
 *
 * @param onAfterLoad A function to run after the omsdk is loaded
 * @returns {boolean} True when the omsdk is supported by the mobile device, false if not
 */
const injectOmSdk = onAfterLoad => {
  const m2mRelsno = Browser.getParameterValue('m2m_relsno');
  const E2Device = m2mRelsno % 2 == 0 ? 'ios' : 'android';

  const validOS =
    (m2mRelsno >= 317 && E2Device === 'android') || (m2mRelsno >= 300 && E2Device === 'ios');

  if (validOS) {
    let doneWithLoad = false;
    const whenDoneWithLoad = () => {
      if (!doneWithLoad) {
        doneWithLoad = true;
        typeof onAfterLoad !== 'function' || onAfterLoad();
      }
    };

    // it's very important that this script is loaded prior to the m2m://interstitial/didFinishPreload call
    // as the client devices will start the omsdk session in that callback...
    // this is one reason why we render the app after the omsdk javascript load
    if (!document.getElementById('omsdk-server-script')) {
      const omsdkJS = document.createElement('script');
      omsdkJS.id = 'omsdk-server-script';
      omsdkJS.type = 'text/javascript';
      omsdkJS.src = '/js/omsdk-js-1.3.34/Service/omsdk-v1.js';
      omsdkJS.onload = whenDoneWithLoad;
      omsdkJS.onerror = whenDoneWithLoad;
      document.head.appendChild(omsdkJS);
    }

    window.injectOmSdkVerificationScript = function() {
      if (!document.getElementById('omsdk-verification-script')) {
        const omsdkVJS = document.createElement('script');
        omsdkVJS.id = 'omsdk-verification-script';
        omsdkVJS.type = 'text/javascript';
        omsdkVJS.src =
          '/js/omsdk-js-1.3.34/Validation-Script/omid-validation-verification-script-v1.js';
        document.head.appendChild(omsdkVJS);
      }
    };
  }
  return validOS;
};

function preload(payload) {
  console.log('>>>>> SDK.preload()');
  //console.log(`NODE_ENV is ${process.env.NODE_ENV}`);

  if (state.hasInitialized) return false;

  // we're changing the payload format in m2m,
  // this is to ensure that E2 can handle the new format
  let localPayload = Payload.reformat(payload);

  // validate payload format
  localPayload = Payload.validate(localPayload);

  window.server.adsControl.setPayload(localPayload);

  console.log('SDK called preload() with params: ', localPayload);

  // log that e2 is loaded
  state.serverPayload = localPayload;
  window.server.logForImprIds('E2_LOADED');
  if (window.server.adsControl.isFirstAd() && state.serverPayload.data) {
    const tryRecording = (data, check, activate) => {
      try {
        if (check(data)) {
          activate(data);
        }
      } catch (e) {
        console.error('Error', e);
      }
    };
    tryRecording(
      state.serverPayload.data,
      Recording.shouldEnableLogRocket,
      Recording.enableLogRocket
    );
  }

  Object.assign(state, {
    payload: localPayload,
    didFinishHide,
    didFinishStartHide,
    didFinishLoad,
    didFinishShow,
    listeners: {},
    on(event, cb) {
      const l = (state.listeners[event] = state.listeners[event] || []);
      l.push(cb);
    },
    event(event, eventPayload) {
      console.log(`Calling Event ${event}`);
      if (state.listeners[event]) {
        console.log('have listeners for event');
        for (const l of state.listeners[event]) {
          l(eventPayload);
        }
      }
    },
    Browser,
    currentAdIndex: window.server.adsControl.currentAdIndex(),
    adsControl: window.server.adsControl,
  });

  // for the purposes of third-party params, the first url is fine.
  const urls = Payload.findAdUrlsInPayload(localPayload);
  let thirdParams = {};
  if (urls.length > 0) {
    const params = queryString.parseUrl(urls[0]);
    thirdParams = params.query;
    if (thirdParams.url) {
      thirdParams = {
        ...thirdParams,
        ...queryString.parseUrl(thirdParams.url).query,
      };
    }
  }

  // clear any preexisting engagement
  ReactDOM.unmountComponentAtNode(Browser.domContainerNode());
  const renderApp = () => {
    ReactDOM.render(
      <EngagementApp {...state} payload={localPayload} />,
      Browser.domContainerNode(),
      () => {
        window.server.notifySdk('didFinishPreload');
      }
    );
  };

  // either inject the omsdk then render
  // or just render the app
  window.server.adsControl.isFirstAd() && injectOmSdk(renderApp) || renderApp();

  return '';
}

function doHeartBeat() {
  if (get(window, 'M2MWebView.onHeartBeat')) {
    // Android
    window.M2MWebView.onHeartBeat();
  } else if (get(window, 'webkit.messageHandlers.onHeartBeat')) {
    // iOS
    window.webkit.messageHandlers.m2mJsHandler.onHeartBeat();
  } else {
    console.warn(`SDK onHeartBeat isn't defined on the client side`);
  }

  return '';
}

/**
 * Should only be called from within E2 manually:
 * - for non-mobile browsers, or
 * - on a manual payload inject.
 *
 * Invoked by the Android SDK as soon as possible after the `m2m://interstitial/didFinishPreload` call.
 * Invoked by the iOS SDK as soon as possible after the `m2m://interstitial/didFinishLoad` call.
 * It is invoked in both cases only at the point the app is in the foreground or coming into the foreground.
 *
 * Given the deviation, the earliest vector needs to be maintained to prevent session loss.
 *
 * The `m2m://interstitial/didFinishPrepareToShow`, called herein, is effectively a boomerang back
 * into the platform sdk that then triggers the tracking sessions for any platform libraries we
 * integrate with (ie, the IAB OM SDK).
 *
 * For the IAB OM SDK, it is imperative that the Service javascript be loaded prior to the platform sdk
 * calling this method.
 *
 * Within a line or two of the sdk calling this method, the Android sdk notifies publisher M2M listeners
 * that an engagement is being viewed.
 *
 * The iOS platform SDK notifies M2M delegates that an engagement is being viewed in response to a
 * notification that is sent when the M2MViewController is presented.
 *
 * @returns {string}
 */
function prepareToShow() {
  // establish perforamnce point in time to reference for E2 execution time
  window.displayPerfPoint =
    window.displayPerfPoint ? window.displayPerfPoint : performance.now();
  console.log('>>>>> SDK.prepareToShow()');

  // logging to server
  window.server.logForImprIds('E2_PREPARE_TO_SHOW');

  /**
   * Immediately in response to this call, the both platforms start
   * any tracking sessions that they will start with native libraries.
   * (ie, IAB OM SDK)
   */
  window.server.notifySdk('didFinishPrepareToShow');
  return '';
}

function logRequestUrl(url) {
  console.log('>>>>> SDK.logRequetUrl()');
  window.server.logRequestUrl(url);
  // another voodoo moment here for android
  // we need to figure out a way to isolate what exactly causes this
  // to be required... it's likely linked to periodic disappearances of the ad
  const parsed = queryString.parseUrl(window.location.href);
  const relsNo = get(parsed, 'query.m2m_relsno');
  if (relsNo % 2 === 1) {
    window.server.notifySdk('ignoreThis');
  }
  return '';
}

function show() {
  console.log('>>>>> SDK.show()');
  // logging to server
  window.server.logForImprIds('E2_SHOW');

  if (!state.hasInitialized) {
    state.hasInitialized = true;
    console.log('calling app opened');

    if (get(state, 'event')) {
      state.event('app_opened');
    }
    if (!document.hasFocus()) {
      window.focus();
    }
  }
  return '';
}

const runUnload = () => {
  console.log('>>>>> SDK.runUnload()');
  if (get(state, 'event')) {
    state.event('unload');
  }
  if (ReactDOM.unmountComponentAtNode(Browser.domContainerNode()))
    console.log('successfully unloaded application()');
  else {
    console.log('did not unload application');
  }
  state.listeners = {};
  state.hasInitialized = false;
  window.server.notifySdk('didFinishUnload');
};

function unload() {
  // skip any further communication with the sdk till after we cycle through the ads.
  if (window.server.adsControl.nextAd()) {
    return;
  }
  if (Browser.isIOS()) {
    /*
     "Note that ending an OMID ad session sends a message to the verification scripts running inside the
     webview supplied by the integration. So that the verification scripts have enough time to handle the
     sessionFinish event, the integration must maintain a strong reference to the webview for at least 1.0
     seconds after ending the session."
     - https://interactiveadvertisingbureau.github.io/Open-Measurement-SDKiOS/#7-stop-the-session
     - https://interactiveadvertisingbureau.github.io/Open-Measurement-SDKAndroid/#8-stop-the-session

     The actual window doesn't get hidden/destroyed till unload() responds with `m2m://interstitial/didFinishUnload`,
     therefore at this vector we should install, for iOS only, a 1.5s to allow OM SDK to finish the session,
     which would have been triggered in `m2m://interstitial/didFinishHide`
     (cond calls [M2M_WebView closeButtonPressed] in iOS, which will then call window.unload())
     X -> didFinishHide(finish om) -> unload() -(our delay here)-> didFinishUnload

     For Android our case is currently covered in the platform code and this above `isIOS` condition should eventually
     include a version check, as it should be covered in the platform there as well.

     This timeout was specifically introduced because we were not consistently seeing the `sessionFinish` event through
     Charles Proxy on iOS from the OM SDK validation script, provided by IAB.
    */
    setTimeout(runUnload, 1500);
  } else {
    runUnload();
  }
  return '';
}

/**
 * If an http(s) request is made by the WebApp but the WebApp is not able
 * to intercept and handle it.
 *
 * In Android, there is an interceptor that catches navigation to http(s): urls,
 * and posts back here with the url.  We then just launch that url in the browser.
 * For this reason, E2 cannot be reloaded using window.document.location.reload,
 * however, it is safe for any arbitrary creative to perform navigation and that
 * will safely wind up in an external browser instance.
 */
function willSendRequestWithURL(url) {
  if (url.startsWith(document.location.href)) {
    console.log('SDK called willSendRequestWithURL(), but it is here so not doing anything');
    return;
  }

  const localUrl = url;
  try {
    console.log('SDK called willSendRequestWithURL()');
  } catch (e) {
    console.log('SDK called willSendRequestWithURL() error', e.message);
  }

  let target = 'browser';

  // test incoming url for deeplink
  // if deeplink, modify insterstitial/click and call to click tracking
  const deeplinkRegex = /^(?!https?:\/\/|\/\/).*:\/\//;
  if (deeplinkRegex.test(localUrl)) {
    console.log('SDK called willSendRequestWithURL() with deeplink', localUrl);
    target = 'deeplink';
    // if deeplink, call to track link
    if (typeof window.callToTrackLink === 'function') {
      window.callToTrackLink();
    }
  }
  // encode all equals signs
  const encodedUrl = encodeURIComponent(localUrl);

  window.server.notifySdk(`click?target=${target}&url=${encodedUrl}`);
  return false;
}

// Android back button calls this window.dismiss, so click the close button in the app
function dismiss(target) {
  console.log("window.dismiss() called with", target);
  const el = document.getElementById('closeButton');
  if (el !== null) {
    el.click();
    window.server.notifySdk('dismissSuccess');
  } else {
    window.server.notifySdk('dismissFail');
  }
};

function actionForCloseInitiated() {
  console.log("actionForCloseInitiated called");
  const el = document.getElementById('closeAdButton');
  if (el !== null) {
    el.click();
    console.log("closeAdButton clicked");
  } else {
    console.log("closeAdButton was not found");
  }
};

const setupSdkFunctions = () => {
  // //////////////////////////////////////////////////////////////////////////////
  /**
   * Lifecycle hooks for the SDK to call.
   */
  Object.assign(window, {
    preload,
    prepareToShow,
    doHeartBeat,
    logRequestUrl,
    show,
    unload,
    willSendRequestWithURL,
    dismiss,
    actionForCloseInitiated,
  });
  // end hooks for the SDK
  // //////////////////////////////////////////////////////////////////////////////
};

export {
  setupSdkFunctions,
  dismiss,
  doHeartBeat,
  injectOmSdk,
  logRequestUrl,
  preload,
  prepareToShow,
  runUnload,
  unload,
  show,
  willSendRequestWithURL,
  actionForCloseInitiated,
};
